"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { UserMenu } from "../auth/user-menu";
import { LanguageSwitcher } from "../language-switcher";
import { SearchButton } from "../search/search-button";
import { normalizeNumberGrouping } from "@/lib/locale-formatting";

interface EventsHeaderProps {
  cityName: string;
  citySlug: string;
  eventCount: number | null;
  breadcrumbs?: {
    label: string;
    items: { name: string; href?: string }[];
  };
}

export function EventsHeader({
  cityName,
  citySlug,
  eventCount,
  breadcrumbs,
}: EventsHeaderProps) {
  const t = useTranslations("Events");
  const tCommon = useTranslations("Common");
  const logo = (
    <Link
      href="/"
      prefetch={false}
      className="flex items-center gap-2 transition-opacity hover:opacity-80"
    >
      <div className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-acid shadow-[0_0_10px_#D1F805]" />
      <span className="whitespace-nowrap font-mono text-xs tracking-[0.2em] text-neutral-500">
        {tCommon("brand")}
      </span>
    </Link>
  );

  return (
    <div className="mb-3">
      <div className="flex items-start justify-between gap-4">
        <div className="mb-2 min-w-0">
          {breadcrumbs ? (
            <nav aria-label={breadcrumbs.label}>
              <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-xs text-neutral-400">
                <li>{logo}</li>
                {breadcrumbs.items.map((item) => (
                  <li key={item.name} className="flex items-center gap-2">
                    <span aria-hidden="true">/</span>
                    {item.href ? (
                      <Link href={item.href} prefetch={false} className="hover:text-acid">
                        {item.name}
                      </Link>
                    ) : (
                      <span aria-current="page" className="text-neutral-200">
                        {item.name}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          ) : logo}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <SearchButton />
          <LanguageSwitcher />
          <UserMenu />
        </div>
      </div>
      <div className="min-w-0">
        <h1 className="font-mono text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
          {cityName}
        </h1>
        <p className="mt-1 font-mono text-sm text-neutral-500">
          {eventCount === null ? (
            <span aria-label={t("loadingShows")} className="inline-block h-4 w-36 rounded bg-neutral-800 align-middle motion-safe:animate-pulse" />
          ) : normalizeNumberGrouping(t("showCount", { count: eventCount }))}
          <span className="mx-2">|</span>
          <Link
            href={`/${citySlug}/add-show`}
            rel="nofollow"
            className="text-acid transition-opacity hover:opacity-80"
          >
            {t("addShowLink")}
          </Link>
        </p>
      </div>
    </div>
  );
}
